class TemporiQuickOrderList extends HTMLElement {
  connectedCallback() {
    if (!window.TemporiQuickOrderCart || !window.TemporiQuantityRules) return;
    this.cartEvents?.abort();
    this.cartEvents = new AbortController();
    document.addEventListener('cart:updated', event => this.cartUpdated(event.detail?.cart), { signal: this.cartEvents.signal });
    if (this.ready) { this.cartUpdated(); return; }
    this.ready = true;
    this.restoreDraft();
    this.addEventListener('change', () => { if (!this.busy && !this.uncertain) this.saveDraft(); });
    this.addEventListener('input', () => { if (!this.busy && !this.uncertain) this.saveDraft(); });
    this.querySelectorAll('[data-order-selection], [data-order-add]').forEach(node => { node.hidden = false; });
    this.querySelectorAll('[data-order-native-title]').forEach(node => { node.hidden = true; });
    this.addEventListener('click', event => {
      if (!event.target.closest('[data-order-add]')) return;
      this.submitRows([...this.querySelectorAll('[data-order-row]')].filter(row => row.querySelector('[data-order-select]')?.checked));
    });
    this.addEventListener('submit', event => {
      if (!event.target.matches('[data-order-form]')) return;
      event.preventDefault();
      this.submitRows([event.target.closest('[data-order-row]')]);
    });
  }
  disconnectedCallback() {
    this.cartEvents?.abort();
    this.cartRequest?.abort();
    this.cartUpdatePending = false;
    this.pendingCart = null;
  }
  cartUpdated(cart) {
    if (this.busy) {
      // The add result still owns selections and remaining units until it settles.
      this.cartUpdatePending = true;
      this.pendingCart = cart;
      return;
    }
    return this.refreshResults(cart);
  }
  async refreshResults(cart) {
    this.cartRequest?.abort();
    this.querySelectorAll('[data-order-result]').forEach(message => { message.hidden = true; });
    if (!this.rowResults?.size) return;
    const request = this.cartRequest = new AbortController();
    try {
      if (!cart) {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { signal: request.signal, cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Cart refresh failed');
        cart = await response.json();
      }
      if (request.signal.aborted || !Array.isArray(cart.items)) return;
      this.querySelectorAll('[data-order-row]').forEach(row => {
        if (!this.rowResults.has(row.dataset.variantId)) return;
        const count = cart.items.filter(item => String(item.variant_id) === row.dataset.variantId).reduce((sum, item) => sum + Number(item.quantity), 0);
        const message = row.querySelector('[data-order-result]');
        message.textContent = this.dataset.rowResult.replace('[count]', count).replace('[remaining]', this.rowResults.get(row.dataset.variantId));
        message.hidden = false;
      });
    } catch (_) { /* Keep unverified counts hidden; order intent and errors remain unchanged. */ }
  }
  readDraft() {
    if (!this.dataset.draftKey) return {};
    try {
      const saved = JSON.parse(sessionStorage.getItem('tempori:quick-order:' + this.dataset.draftKey));
      if (!saved || !Number.isFinite(saved.expires) || saved.expires < Date.now() || saved.expires > Date.now() + 1800000) return {};
      return saved.rows && typeof saved.rows === 'object' && !Array.isArray(saved.rows) ? saved.rows : {};
    } catch (_) { return {}; }
  }
  writeDraft(rows) {
    if (!this.dataset.draftKey) return;
    try { sessionStorage.setItem('tempori:quick-order:' + this.dataset.draftKey, JSON.stringify({ expires: Date.now() + 1800000, rows })); } catch (_) { /* Storage is optional. */ }
  }
  restoreDraft() {
    const saved = this.readDraft();
    this.querySelectorAll('[data-order-row]').forEach(row => {
      const selection = row.querySelector('[data-order-select]');
      const input = row.querySelector('[name="quantity"]');
      const value = saved[row.dataset.variantId];
      if (!selection || selection.disabled || !input || !value || !/^\d{1,9}$/.test(String(value.quantity))) return;
      selection.checked = value.selected === true;
      input.value = String(value.quantity);
    });
  }
  saveDraft() {
    if (!this.dataset.draftKey) return;
    const saved = this.readDraft();
    this.querySelectorAll('[data-order-row]').forEach(row => {
      const selection = row.querySelector('[data-order-select]');
      const input = row.querySelector('[name="quantity"]');
      if (!selection || selection.disabled || !input || !/^\d{1,9}$/.test(input.value)) delete saved[row.dataset.variantId];
      else saved[row.dataset.variantId] = { selected: selection.checked, quantity: input.value };
    });
    this.writeDraft(saved);
  }
  forgetRows(rows) {
    const saved = this.readDraft();
    rows.forEach(row => { delete saved[row.dataset.variantId]; });
    this.writeDraft(saved);
  }
  async submitRows(rows) {
    if (this.busy || this.uncertain) return;
    const status = this.querySelector('[data-order-status]');
    if (!rows.length) { status.textContent = this.dataset.empty; return; }
    const inputs = rows.map(row => row.querySelector('[name="quantity"]'));
    if (inputs.some(input => !input || !input.reportValidity())) return;
    const payload = rows.map((row, index) => ({ id: row.dataset.variantId, quantity: Number(inputs[index].value), rule: window.TemporiQuantityRules.ruleFor(inputs[index]) }));
    // Remove submitted intent before the request: uncertain outcomes must never
    // restore a selection that may already have been added to the cart.
    this.forgetRows(rows);
    this.cartRequest?.abort();
    this.querySelectorAll('[data-order-result]').forEach(message => { message.hidden = true; });
    this.rowResults ||= new Map();
    rows.forEach(row => { this.rowResults.delete(row.dataset.variantId); });
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    const controls = [...this.querySelectorAll('button, input')].filter(control => !control.disabled);
    controls.forEach(control => { control.disabled = true; });
    status.textContent = this.dataset.adding;
    let focusTarget = status;
    try {
      const result = await window.TemporiQuickOrderCart.add(payload);
      this.uncertain = result.status === 'uncertain';
      status.textContent = this.dataset[result.status] || this.dataset.unavailable;
      if (result.cart) {
        window.TemporiQuantityRules.applyCart(result.cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: result.cart } }));
      }
      for (const item of result.items || []) {
        const row = rows.find(candidate => candidate.dataset.variantId === item.id);
        const message = row.querySelector('[data-order-result]');
        this.rowResults.set(item.id, item.remaining);
        message.hidden = false;
        message.textContent = this.dataset.rowResult.replace('[count]', item.cartQuantity).replace('[remaining]', item.remaining);
        row.querySelector('[data-order-select]').checked = item.remaining > 0;
        if (item.remaining > 0) row.querySelector('[name="quantity"]').value = item.remaining;
      }
      if (result.status === 'complete' || result.status === 'partial' || this.uncertain) {
        // Until the drawer has fresh server HTML, its native cart links are authoritative.
        await window.NavelisCart?.refresh?.(result.cart);
      }
      if (result.invalidIds?.length) focusTarget = rows.find(row => row.dataset.variantId === result.invalidIds[0])?.querySelector('[name="quantity"]') || status;
    } catch (_) {
      this.uncertain = true;
      status.textContent = this.dataset.uncertain;
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      if (!this.uncertain) {
        controls.forEach(control => { control.disabled = false; });
        this.saveDraft();
      }
      focusTarget.focus();
      if (this.cartUpdatePending) {
        this.cartUpdatePending = false;
        const cart = this.pendingCart;
        this.pendingCart = null;
        this.refreshResults(cart);
      }
    }
  }
}
if (!customElements.get('quick-order-list')) customElements.define('quick-order-list', TemporiQuickOrderList);
